import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { convertAmountToFrontend } from "../../../helpers/convert";
import {
  getActiveCertificatesRequest,
  getRedemptionUsableCertificatesRequest,
  setCertificateFilterExportStartDate,
  setCertificateFilterProductionPeriod,
  setCertificateFilterResourceType,
  setCertificateFilterStation,
} from "../../../redux/actions/certificates";
import {
  getCitiesRequest,
  getDistrictsRequest,
} from "../../../redux/actions/geo";

import moment from "moment";
import Button from "../../../components/Button";
import ComboBoxStation from "../../../components/ComboBoxStation";
import DataTable from "../../../components/DataTable";
import DataTableServerSideRendering from "../../../components/DataTable/DataTableServerSideRendering";
import EmptyState from "../../../components/EmptyState/EmptyState";
import { Input, Select, Textarea } from "../../../components/Inputs";
import Loading from "../../../components/Loading";
import ModalCertificate from "../../../components/Modal/ModalCertificate";
import ModalConfirmation from "../../../components/Modal/ModalConfirmation";
import {
  beneficiaryTypes,
  redemptionPurpose,
  resourceTypes,
  sectorTypes,
} from "../../../constants/selects";
import { monthsAndResourceTypes } from "../../../constants/translations";
import { API_GET, API_POST } from "../../../helpers/api";
import { setNotification } from "../../../helpers/notification";
import {
  getConsumptionDataRequested,
  setFilterConsumptionPeriod,
  historyRedemptionSearchRequest,
} from "../../../redux/actions/redemptions";
import { controlConstants } from "../../../constants/controlConstants";

export default function RedemptionOperations({ t }) {
  const dispatch = useDispatch();
  const certificates = useSelector((state) => state.certificates.activeList);
  const { items: historyRedemptions } = useSelector(
    (state) => state.redemptions.historyRedemptions
  );
  const historyRedemptionsDataCheck = historyRedemptions?.length > 0;

  const allCertificates = useSelector((state) => state.certificates);
  const cities = useSelector((state) => state.geo.cities);
  const districts = useSelector((state) => state.geo.districts);
  const certificatesK1 = useSelector(
    (state) => state.certificates.k1RedemptionUsable
  );

  const loading = useSelector((state) => state.certificates.loading);
  const selectedOrganization = useSelector(
    (state) => state.authorization.user.organization
  );
  const consumptionData = useSelector(
    (state) => state.redemptions.consumptionData
  );
  // Filters
  const filterStation = useSelector(
    (state) => state.certificates.filterStation
  );
  const filterResourceType = useSelector(
    (state) => state.certificates.filterResourceType
  );
  const filterProductionPeriodMonth = useSelector(
    (state) => state.certificates.filterProductionPeriodMonth
  );
  const filterProductionPeriodYear = useSelector(
    (state) => state.certificates.filterProductionPeriodYear
  );

  const filterExportStartDateMonth = useSelector(
    (state) => state.certificates.filterExportStartDateMonth
  );
  const filterExportStartDateYear = useSelector(
    (state) => state.certificates.filterExportStartDateYear
  );
  const filterConsumptionPeriod = useSelector(
    (state) => state.redemptions.consumptionPeriod
  );

  const documentCount = useSelector(
    (state) => state.certificates.documentCount
  );
  const blockedDocumentCount = useSelector(
    (state) => state.certificates.blockedDocumentCount
  );

  const [redemptionCertificates, setRedemptionCertificates] = useState([]);
  const [historyRedemptionsList, setHistoryRedemptionsList] = useState([]);
  const [checkRedemptionStatusMessage, setCheckRedemptionStatusMessage] =
    useState();

  const [inspectedCertificateId, setInspectedCertificateId] = useState();

  const [beneficiaryInformations, setBeneficiaryInformations] = useState({
    title: selectedOrganization.organizationName,
    startPeriod: "",
    taxId: "",
    endPeriod: "",
    redemptionPurpose: "",
    beneficiaryType: "",
    address: "",
    city: "",
    district: "",
    sector: "",
  });

  const [description, setDescription] = useState();
  const [title, setTitle] = useState("");
  const [taxId, setTaxId] = useState("");

  const [sendRedemptionConfirmationModal, setSendRedemptionConfirmationModal] =
    useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [organizationType, setOrganizationType] = useState("");

  const [datePickerLimitations, setDatePickerLimitations] = useState({
    startPeriod: { min: "", max: "" },
    endPeriod: { min: "", max: "" },
  });

  const [activePage, setActivePage] = useState(0);
  const [sortedColumn, setSortedColumn] = useState("productionPeriod");
  const [sortDirection, setSortDirection] = useState("desc");
  const translations = monthsAndResourceTypes(t);
  const selectResourceType = resourceTypes(t);
  const redemptionPurposeOptions = redemptionPurpose(t);
  const beneficiaryTypeOptions = beneficiaryTypes(t);
  const sectorTypeOptions = sectorTypes(t);

  const activeCertificatesColumns = [
    {
      name: t("pages.default.certificates.table.stationName"),
      selector: "stationName",
      sortable: true,
      cell: (row) => {
        return row.stationName ?? "stationName";
      },
    },
    {
      name: t("pages.default.certificates.table.resourceType"),
      selector: "resourceType",
      sortable: true,
      cell: (row) => {
        return translations[row.resourceType];
      },
    },
    {
      name: t("pages.default.certificates.table.productionPeriod"),
      selector: "productionPeriod",
      sortable: true,
      cell: (row) => {
        return `${translations[row.month]}/${row.year}`;
      },
    },
    {
      name: t("pages.default.certificates.table.exportPeriod"),
      selector: "exportDate",
      sortable: true,
      cell: (row) =>
        moment(row.exportDate).isValid()
          ? moment(row.exportDate).format("DD/MM/YYYY HH:mm:ss")
          : "-",
    },
    {
      name: t("pages.default.certificates.table.durationOfUse"),
      selector: "durationOfUse",
      sortable: true,
      cell: (row) => {
        return convertAmountToFrontend(row.durationOfUse);
      },
    },
    {
      name: t("pages.default.certificates.table.documentCount"),
      selector: "amount",
      sortable: true,
      cell: (row) => {
        return convertAmountToFrontend(row.amount);
      },
    },
    {
      name: t("pages.default.certificates.table.blockedAmount"),
      selector: "blockedAmount",
      sortable: true,
      cell: (row) => {
        return convertAmountToFrontend(row.blockedAmount);
      },
    },
    {
      name: t("pages.default.certificates.table.process"),
      selector: "process",
      sortable: false,
      width: "140px",
      cell: (row) => {
        return (
          <div style={{ display: "flex" }}>
            <Button
              title={t("ui.default.review")}
              suffixIcon="search"
              type="secondary"
              onClick={() => inspectCertificate(row)}
            />
            <Button
              title={t("ui.default.add")}
              suffixIcon="plus"
              type="primary"
              onClick={() => addCertificateToRedemption(row)}
              disabled={addCertificateButtonIsDisabled(row)}
            />
          </div>
        );
      },
    },
  ];

  const redemptionCertificatesColumns = [
    {
      name: t("pages.default.certificates.table.stationName"),
      selector: "stationName",
      sortable: true,
    },
    {
      name: t("pages.default.certificates.table.resourceType"),
      selector: "resourceType",
      sortable: true,
      cell: (row) => {
        return translations[row.resourceType];
      },
    },
    {
      name: t("pages.default.certificates.table.productionPeriod"),
      selector: "productionPeriod",
      sortable: true,
      cell: (row) => {
        return `${translations[row.month]}/${row.year}`;
      },
    },
    {
      name: t("pages.default.certificates.table.durationOfUse"),
      selector: "durationOfUse",
      sortable: true,
    },
    {
      name: t("pages.default.certificates.table.documentCount"),
      selector: "amount",
      sortable: false,
      cell: (row) => {
        return (
          <Input
            type="number"
            min={1}
            max={row.amount}
            pattern="[0-9]*"
            value={row.redemptionAmount ? row.redemptionAmount : ""}
            placeholder={`Max ${row.amount}`}
            onChange={(e) => redemptionAmountChange(e, row)}
          />
        );
      },
    },
    {
      name: t("pages.default.certificates.table.process"),
      selector: "process",
      sortable: false,
      width: "140px",
      cell: (row) => {
        return (
          <div style={{ display: "flex" }}>
            <Button
              title={t("ui.default.remove")}
              suffixIcon="minus"
              type="destructive"
              onClick={() => removeCertificateFromRedemption(row)}
            />
          </div>
        );
      },
    },
  ];

  const consumptionDataColumns = [
    {
      name: t("pages.default.redemption.table.consumptionPeriod"),
      selector: "consumptionPeriod",
      sortable: false,
      cell: (row) => {
        return moment(row.consumptionPeriod).format("MMMM YYYY");
      },
    },
    {
      name: t("pages.default.redemption.table.consumptionAmount"),
      selector: "amount",
      sortable: false,
      cell: (row) => {
        return Math.ceil(row.consumptionAmount);
      },
    },
    {
      name: t("pages.default.redemption.table.status"),
      selector: "status",
      sortable: false,
      cell: (row) => {
        return translations[row.status];
      },
    },
  ];
  const pageInfoDataColumns = [
    {
      name: t("pages.default.redemption.table.consumptionPeriod"),
      selector: "consumptionPeriod",
      sortable: false,
      width: "400px",
      cell: (row) => {
        return moment(row.consumptionPeriod).format("MMMM YYYY");
      },
    },
    {
      name: t("pages.default.redemption.table.status"),
      selector: "redemptionStatus",
      sortable: false,
      cell: (row) => {
        return translations[row.redemptionStatus];
      },
    },
  ];

  useEffect(() => {
    // if organization K1 redemption purpose should be IFSA_YESIL_TARIFE, other types should be IFSA
    let redemptionPurpose = "";
    let beneficiaryType = "";
    let startPeriod = "";
    let endPeriod = "";
    if (selectedOrganization.yekgLicenseType === controlConstants?.K1) {
      redemptionPurpose = "IFSA_YESIL_TARIFE";
      beneficiaryType = "K1_PRODUCER";
      if (filterConsumptionPeriod) {
        startPeriod = filterConsumptionPeriod;
        endPeriod = filterConsumptionPeriod;
      }
    } else if (
      selectedOrganization.yekgLicenseType ===
      controlConstants?.CHARGING_NETWORK_OPERATOR
    ) {
      redemptionPurpose = "IFSA";
      beneficiaryType = "PRODUCER";
    } else {
      redemptionPurpose = "IFSA";
      beneficiaryType = "CONSUMER";
    }

    if (
      selectedOrganization.yekgLicenseType ===
      controlConstants?.CHARGING_NETWORK_OPERATOR
    ) {
      // remove beneficiary informations set redemption purpose for organization type
      setBeneficiaryInformations({
        title: "",
        startPeriod: startPeriod,
        taxId: taxId,
        endPeriod: endPeriod,
        redemptionPurpose: redemptionPurpose,
        beneficiaryType: beneficiaryType,
        address: "",
        city: "",
        district: "",
        sector: "",
      });
    } else {
      // remove beneficiary informations set redemption purpose for organization type
      setBeneficiaryInformations({
        title: "",
        startPeriod: startPeriod,
        taxId: "",
        endPeriod: endPeriod,
        redemptionPurpose: redemptionPurpose,
        beneficiaryType: beneficiaryType,
        address: "",
        city: "",
        district: "",
        sector: "",
      });
    }

    // set organization type for display or not green tariff field
    setOrganizationType(selectedOrganization.yekgLicenseType);

    filterCertificates();
    getCities();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Start period - end Period min max Calculation
  useEffect(() => {
    setTitle(
      organizationType === controlConstants?.K1 ||
        organizationType === controlConstants?.CHARGING_NETWORK_OPERATOR
        ? selectedOrganization.organizationName
        : ""
    );

    startEndDateInitialMinMaxCalculation();
  }, []);
  const startEndDateInitialMinMaxCalculation = () => {
    let now = new Date();
    let sPMin, sPMax, ePMin, ePMax;
    let aprilOne = new Date(now.getFullYear(), 3, 1);
    let firstDayOfYear = new Date(now.getFullYear(), 0, 1);
    if (now >= aprilOne) {
      sPMin = new Date(now.getFullYear(), 0);
      ePMin = new Date(now.getFullYear(), 0);
    } else {
      if (now > firstDayOfYear) {
        sPMin = new Date(now.getFullYear() - 1, 0);
        ePMin = new Date(now.getFullYear() - 1, 0);
      } else {
        sPMin = new Date(now.getFullYear(), 0);
        ePMin = new Date(now.getFullYear(), 0);
      }
    }
    if (now.getDate() < 15) {
      sPMax = moment(now).subtract("2", "months");
      ePMax = moment(now).subtract("2", "months");
    } else {
      sPMax = moment(now).subtract("1", "months");
      ePMax = moment(now).subtract("1", "months");
    }
    setDatePickerLimitations({
      startPeriod: {
        min: moment(sPMin).format("YYYY-MM"),
        max: moment(sPMax).format("YYYY-MM"),
      },
      endPeriod: {
        min: moment(ePMin).format("YYYY-MM"),
        max: moment(ePMax).format("YYYY-MM"),
      },
    });
  };

  const filterConsumptionPeriodChange = (e) => {
    setHistoryRedemptionsList([]);

    dispatch(setFilterConsumptionPeriod(e.target.value));
    setBeneficiaryInformations({
      ...beneficiaryInformations,
      startPeriod: e.target.value,
      endPeriod: e.target.value,
    });
  };
  const getConsumptionData = () => {
    if (!filterConsumptionPeriod) {
      setNotification("error", "Lütfen Tüketim Dönemi Seçiniz!");
      return;
    }
    dispatch(
      getConsumptionDataRequested({
        organizationId: selectedOrganization.organizationId,
        period: filterConsumptionPeriod,
      })
    );
  };
  const filterChange = (e) => {
    let splitted = [];
    let payload = {};
    splitted = e.target.value.split("-");
    payload = {
      month: splitted[1] || "",
      year: splitted[0] || "",
    };
    switch (e.target.name) {
      case "exportStartDate":
        dispatch(setCertificateFilterExportStartDate(payload));
        break;
      case "productionPeriod":
        dispatch(setCertificateFilterProductionPeriod(payload));
        break;
      case "resourceType":
        dispatch(setCertificateFilterResourceType(e.target.value));
        break;
      default:
        break;
    }
  };
  const filterActiveRedemptions = () => {
    let startDate = filterConsumptionPeriod.split("-");
    let endDate = filterConsumptionPeriod.split("-");
    if (filterConsumptionPeriod) {
      dispatch(
        historyRedemptionSearchRequest({
          organizationId: selectedOrganization
            ? selectedOrganization.organizationId
            : "",
          title: null,
          status: "COMPLETED",
          startMonth: startDate[1] ? parseInt(startDate[1]) : "",
          startYear: startDate[0] ? startDate[0] : "",
          endMonth: endDate[1] ? parseInt(endDate[1]) : "",
          endYear: endDate[0] ? endDate[0] : "",
        })
      );
    }
  };

  useEffect(() => {
    if (historyRedemptions) {
      setHistoryRedemptionsList([
        historyRedemptionsDataCheck
          ? {
              consumptionPeriod: filterConsumptionPeriod,
              redemptionStatus: "COMPLETED",
            }
          : {
              consumptionPeriod: filterConsumptionPeriod,
              redemptionStatus: "INITIAL",
            },
      ]);
    }
  }, [historyRedemptionsDataCheck, historyRedemptions]);

  const selectStation = (station) => {
    dispatch(setCertificateFilterStation(station));
  };

  const inspectCertificate = (row) => {
    setInspectedCertificateId(row.id);
  };

  const addCertificateToRedemption = (row) => {
    //   Deep copy
    let redemptionCertificatesCopy = JSON.parse(
      JSON.stringify(redemptionCertificates)
    );
    redemptionCertificatesCopy.push({
      id: row.id,
      stationName: row.stationName,
      resourceType: row.resourceType,
      productionPeriod: row.productionPeriod,
      month: row.month,
      year: row.year,
      amount: row.amount,
      durationOfUse: row.durationOfUse,
      redemptionAmount: 0,
    });
    setRedemptionCertificates(redemptionCertificatesCopy);
    setTitle(
      organizationType === controlConstants?.K1 ||
        organizationType === controlConstants?.CHARGING_NETWORK_OPERATOR
        ? row.organizationName
        : ""
    );
    setTaxId(
      organizationType === controlConstants?.K1 ||
        organizationType === controlConstants?.CHARGING_NETWORK_OPERATOR
        ? row.organizationTaxNo
        : ""
    );
    setBeneficiaryInformations({
      ...beneficiaryInformations,
      title,
    });
  };

  const addCertificateButtonIsDisabled = (row) => {
    if (row.amount === 0) {
      return true;
    }
    let certificate = redemptionCertificates.filter((certificate) => {
      return certificate.id === row.id;
    });
    return certificate[0];
  };

  const removeCertificateFromRedemption = (row) => {
    //   Deep copy
    let redemptionCertificatesCopy = JSON.parse(
      JSON.stringify(redemptionCertificates)
    );
    let redemptionCertificateIndex = redemptionCertificatesCopy.findIndex(
      (certificate) => {
        return certificate.id === row.id;
      }
    );
    redemptionCertificatesCopy.splice(redemptionCertificateIndex, 1);
    setRedemptionCertificates(redemptionCertificatesCopy);
  };

  const redemptionAmountChange = (e, row) => {
    //   Deep copy
    let redemptionCertificatesCopy = JSON.parse(
      JSON.stringify(redemptionCertificates)
    );
    // eslint-disable-next-line array-callback-return
    redemptionCertificatesCopy.map((certificate) => {
      if (certificate.id === row.id) {
        certificate.redemptionAmount = parseInt(e.target.value);
      }
    });
    setRedemptionCertificates(redemptionCertificatesCopy);
  };

  const descriptionChange = (e) => {
    setDescription(e.target.value);
  };

  const beneficiaryInformationsChange = (e) => {
    if (e.target.name === "title") {
      setBeneficiaryInformations({
        ...beneficiaryInformations,
        title: e.target.value,
      });
      setTitle(e.target.value);
    }
    if (e.target.name === "city") {
      dispatch(getDistrictsRequest({ cityId: e.target.value }));
      setBeneficiaryInformations({
        ...beneficiaryInformations,
        city: e.target.value,
        district: "",
      });
      return;
    }

    if (e.target.name === "startPeriod") {
      setBeneficiaryInformations({
        ...beneficiaryInformations,
        [e.target.name]: e.target.value,
      });
      let ePMin = e.target.value;
      setDatePickerLimitations({
        ...datePickerLimitations,
        endPeriod: { ...datePickerLimitations.endPeriod, min: ePMin },
      });
      return;
    }
    if (e.target.name === "endPeriod") {
      setBeneficiaryInformations({
        ...beneficiaryInformations,
        [e.target.name]: e.target.value,
      });
      let sPMax = e.target.value;
      setDatePickerLimitations({
        ...datePickerLimitations,
        startPeriod: { ...datePickerLimitations.startPeriod, max: sPMax },
      });
      return;
    }
    setBeneficiaryInformations({
      ...beneficiaryInformations,
      [e.target.name]: e.target.value,
      title: title,
    });
  };

  const checkRedemptionStatus = async () => {
    try {
      const res = await API_GET("certificate/redemption/warning-message", {
        organizationId: selectedOrganization.organizationId,
        taxNumber:
          organizationType === controlConstants?.CHARGING_NETWORK_OPERATOR
            ? taxId
            : beneficiaryInformations.taxId,
      });
      setCheckRedemptionStatusMessage(res.data);
    } catch (error) {
      if (error.response) {
        setNotification("error", error.response.data.message);
      } else {
        setNotification("error", "Bilinmeyen hata oluştu");
      }
    }
  };

  const sendRedemptionConfirmationToggle = () => {
    checkRedemptionStatus();

    if (!validateRedemptionRequest()) {
      return false;
    }
    setSendRedemptionConfirmationModal(true);
  };

  const validateRedemptionRequest = () => {
    let redemptionCertificatesHasNull = redemptionCertificates
      .map((item) => {
        return !item.redemptionAmount;
      })
      .includes(true);

    let redemptionTotal = redemptionCertificates.reduce((acc, certificate) => {
      acc += parseInt(
        certificate.redemptionAmount ? certificate.redemptionAmount : 0
      );
      return acc;
    }, 0);
    if (
      organizationType === controlConstants?.K1 &&
      redemptionTotal < Math.ceil(consumptionData[0].consumptionAmount)
    ) {
      setNotification(
        "error",
        t("pages.default.redemption.errors.consumptionAmount", {
          amount: Math.ceil(consumptionData[0].consumptionAmount),
        })
      );
      return false;
    }

    if (redemptionCertificatesHasNull) {
      setNotification(
        "error",
        t("pages.default.redemption.errors.fillAllCertificates")
      );
      return false;
    }
    if (
      organizationType === controlConstants?.K1 ||
      organizationType === controlConstants?.CHARGING_NETWORK_OPERATOR
    ) {
    } else {
      if (!beneficiaryInformations.title || !beneficiaryInformations.title.trim()) {
        setNotification(
          "error",
          t("pages.default.redemption.errors.fillTitle")
        );
        return false;
      }
    }
    if (organizationType === controlConstants?.CHARGING_NETWORK_OPERATOR) {
    } else {
      if (!beneficiaryInformations.taxId) {
        setNotification("error", t("pages.default.redemption.errors.taxId"));
        return false;
      }
    }
    if (
      beneficiaryInformations.taxId &&
      (beneficiaryInformations.taxId.toString().length < 10 ||
        beneficiaryInformations.taxId.toString().length > 11)
    ) {
      setNotification(
        "error",
        t("pages.default.redemption.errors.taxIdNotTrue")
      );
      return false;
    }

    if (!beneficiaryInformations.startPeriod) {
      setNotification("error", t("pages.default.redemption.errors.startDate"));
      return false;
    }

    if (!beneficiaryInformations.endPeriod) {
      setNotification("error", t("pages.default.redemption.errors.endDate"));
      return false;
    }

    if (!beneficiaryInformations.redemptionPurpose) {
      setNotification("error", t("pages.default.redemption.errors.purpose"));
      return false;
    }

    if (!beneficiaryInformations.beneficiaryType) {
      setNotification(
        "error",
        t("pages.default.redemption.errors.beneficaryType")
      );
      return false;
    }

    if (!beneficiaryInformations.address) {
      setNotification("error", t("pages.default.redemption.errors.address"));
      return false;
    }

    if (!beneficiaryInformations.city) {
      setNotification("error", t("pages.default.redemption.errors.city"));
      return false;
    }

    if (!beneficiaryInformations.district) {
      setNotification("error", t("pages.default.redemption.errors.district"));
      return false;
    }

    if (!beneficiaryInformations.sector) {
      setNotification("error", t("pages.default.redemption.errors.sector"));
      return false;
    }
    return true;
  };

  const sendRedemptionRequest = async () => {
    setRequestLoading(true);
    try {
      let createRedemptionDetailRequests = redemptionCertificates.reduce(
        (acc, value) => {
          acc.push({
            amount: value.redemptionAmount,
            yekgCertificateId: value.id,
          });
          return acc;
        },
        []
      );
      await API_POST("certificate/redemption", {
        organizationId: selectedOrganization.organizationId,
        createRedemptionDetailRequests: createRedemptionDetailRequests,
        description: description,
        term: filterConsumptionPeriod
          ? filterConsumptionPeriod.replace("-", "")
          : null,
        beneficiaryPartyRequest: {
          title,
          taxOrIdentityNumber:
            organizationType === controlConstants?.CHARGING_NETWORK_OPERATOR
              ? taxId
              : beneficiaryInformations.taxId,
          redemptionPurpose: beneficiaryInformations.redemptionPurpose,
          address: beneficiaryInformations.address,
          city: cities?.find(
            (item) => item.value === beneficiaryInformations.city
          )?.name,
          district: beneficiaryInformations.district,
          sector: beneficiaryInformations.sector,
          consumptionStartYear:
            beneficiaryInformations.startPeriod.split("-")[0],
          consumptionStartMonth:
            beneficiaryInformations.startPeriod.split("-")[1],
          consumptionEndYear: beneficiaryInformations.endPeriod.split("-")[0],
          consumptionEndMonth: beneficiaryInformations.endPeriod.split("-")[1],
          beneficiaryPartyType:
            organizationType === controlConstants?.K1
              ? beneficiaryInformations.beneficiaryType.split("_")[1]
              : beneficiaryInformations.beneficiaryType,
        },
      });
      setNotification("success", t("pages.default.redemption.success"));
      // remove beneficiaryInformations
      setBeneficiaryInformations({
        title: "",
        startPeriod: "",
        taxId: "",
        endPeriod: "",
        redemptionPurpose: beneficiaryInformations.redemptionPurpose,
        beneficiaryType:
          organizationType === controlConstants?.K1
            ? "K1_PRODUCER"
            : "CONSUMER",
        address: "",
        city: "",
        district: "",
      });
      // remove redemption certificates
      setRedemptionCertificates([]);
      // get active certificates again
      filterCertificates();
      // get start end date min max calculation again
      startEndDateInitialMinMaxCalculation();
      // close confirmation modal
      setSendRedemptionConfirmationModal(false);
      setRequestLoading(false);
    } catch (error) {
      if (
        error.response.data.message ===
        "There ıs already a redemption for this account"
      ) {
        setNotification(
          "error",
          t("pages.default.redemption.errors.accountAlreadyHaveRedemption")
        );
        setRequestLoading(false);
      } else {
        setNotification("error", error.response.data.message);
        setRequestLoading(false);
      }
    }
  };

  const filterCertificates = () => {
    let params = {
      stationId: filterStation ? filterStation.stationId : "",
      resourceType: filterResourceType,
      month: filterProductionPeriodMonth,
      year: filterProductionPeriodYear,
      exportStartDateMonth: filterExportStartDateMonth,
      exportStartDateYear: filterExportStartDateYear,
      certificateStatus: ["ACTIVE"],
    };
    if (selectedOrganization.yekgLicenseType === controlConstants?.K1) {
      dispatch(
        getRedemptionUsableCertificatesRequest({
          ...params,
          organizationId: selectedOrganization
            ? selectedOrganization.organizationId
            : "",
        })
      );
    } else {
      dispatch(getActiveCertificatesRequest({ ...params }));
    }
  };
  const getCities = () => {
    dispatch(getCitiesRequest({}));
  };

  const showActiveCertificates = () => {
    if (organizationType !== controlConstants?.K1) {
      return true;
    }
    if (
      organizationType === controlConstants?.K1 &&
      consumptionData.length > 0
    ) {
      return true;
    }
    return false;
  };
  const handleClick = (activePage, selector, direction) => {
    console.log("page", activePage, selector, direction);
    setActivePage(activePage);

    let params = {
      organizationId: selectedOrganization
        ? selectedOrganization.organizationId
        : "",
      stationId: filterStation ? filterStation.stationId : "",
      resourceType: filterResourceType,
      month: filterProductionPeriodMonth,
      year: filterProductionPeriodYear,
      exportStartDateMonth: filterExportStartDateMonth,
      exportStartDateYear: filterExportStartDateYear,
      certificateStatus: ["ACTIVE"],
      page: activePage,
      selector: selector,
      direction: direction,
    };
    if (organizationType === controlConstants?.K1) {
      dispatch(
        getRedemptionUsableCertificatesRequest({
          ...params,
        })
      );
    } else {
      dispatch(getActiveCertificatesRequest({ ...params }));
    }
  };
  return (
    <>
      {organizationType === controlConstants?.CHARGING_NETWORK_OPERATOR ? (
        <>
          <div className="km__p--redemption-operations-filter-container">
            <div className="km__p--redemption-title">
              {t("pages.default.redemption.redemptionOperationsTitle")}
            </div>
            <div className="km__p--redemption-operations-filters">
              <Input
                type="month"
                placeholder={t(
                  "pages.default.redemption.table.consumptionPeriod"
                )}
                min={datePickerLimitations.startPeriod.min}
                max={datePickerLimitations.startPeriod.max}
                value={filterConsumptionPeriod}
                onChange={filterConsumptionPeriodChange}
              />
              <Button
                type="primary"
                label={t("ui.default.filter")}
                onClick={filterActiveRedemptions}
              />
            </div>
          </div>
          <div className="km__p--card">
            <DataTable
              rowKey="id"
              columns={pageInfoDataColumns}
              data={historyRedemptionsList}
            />
          </div>
        </>
      ) : (
        <div className="km__p--redemption-operations-filter-container">
          <h5 className="km__u--text-has-emphasis">
            {t("pages.default.redemption.redemptionOperationsTitle")}
          </h5>
        </div>
      )}
      {organizationType === controlConstants?.K1 && (
        <div className="km__p--card">
          <div className="km__p--redemption-operations-filter-container">
            <div className="km__p--redemption-title">
              {t("pages.default.redemption.greenTariffConsumptionAmount")}
            </div>
            <div className="km__p--redemption-operations-filters">
              <Input
                type="month"
                placeholder={t(
                  "pages.default.redemption.table.consumptionPeriod"
                )}
                value={filterConsumptionPeriod}
                onChange={filterConsumptionPeriodChange}
              />
              <Button
                type="primary"
                label={t("ui.default.filter")}
                onClick={getConsumptionData}
              />
            </div>
          </div>
          <DataTable
            rowKey="id"
            columns={consumptionDataColumns}
            data={consumptionData}
          />
          {consumptionData.length === 0 && (
            <EmptyState
              icon="file-text"
              text={t("pages.default.redemption.consumptionNotFound")}
            />
          )}
        </div>
      )}
      <div className="km__p--card">
        {showActiveCertificates() && (
          <div className="km__p--redemption-operations-filter-container">
            <div className="km__p--redemption-title">
              {t("pages.default.redemption.activeCertificates")}
            </div>
            <div className="km__p--redemption-operations-filters">
              <ComboBoxStation
                t={t}
                selectedStation={filterStation}
                selectStation={selectStation}
              />
              <Input
                type="month"
                placeholder={t(
                  "pages.default.certificates.filters.allProductionPeriod"
                )}
                name="productionPeriod"
                value={
                  filterProductionPeriodYear
                    ? `${filterProductionPeriodYear}-${filterProductionPeriodMonth}`
                    : ""
                }
                onChange={filterChange}
              />
              <Input
                type="month"
                placeholder={t(
                  "pages.default.certificates.filters.exportStartDate"
                )}
                name="exportStartDate"
                placement="bottom-end"
                value={
                  filterExportStartDateYear
                    ? `${filterExportStartDateYear}-${filterExportStartDateMonth}`
                    : ""
                }
                onChange={filterChange}
              />
              <Select
                options={selectResourceType}
                name="resourceType"
                defaultValue={filterResourceType}
                onChange={filterChange}
              />
              <Button
                type="primary"
                label={t("ui.default.filter")}
                onClick={filterCertificates}
              />
            </div>
          </div>
        )}
        {showActiveCertificates() &&
          (certificates.length > 0 ||
            (certificatesK1?.items && certificatesK1?.items.length > 0)) &&
          !loading && (
            <DataTableServerSideRendering
              columns={activeCertificatesColumns}
              data={
                organizationType === controlConstants?.K1
                  ? certificatesK1?.items
                  : certificates
              }
              rowKey="id"
              sortedColumn={sortedColumn}
              setSortedColumn={setSortedColumn}
              sortDirection={sortDirection}
              setSortDirection={setSortDirection}
              pagination
              activePage={activePage}
              totalPageCount={
                organizationType === controlConstants?.K1
                  ? certificatesK1?.total?.totalPageCount
                  : allCertificates.totalPageCount
              }
              handleFetchData={handleClick}
              subTotals={[
                {
                  totalCount:
                    organizationType === controlConstants?.K1
                      ? certificatesK1?.documentCount?.totalCount
                      : documentCount?.totalCount,
                  countInPage:
                    organizationType === controlConstants?.K1
                      ? certificatesK1?.documentCount?.countInPage
                      : documentCount.countInPage,
                  sumIndex: 5,
                },
                {
                  totalCount:
                    organizationType === controlConstants?.K1
                      ? certificatesK1?.blockedDocumentCount?.totalCount
                      : blockedDocumentCount.totalCount,
                  countInPage:
                    organizationType === controlConstants?.K1
                      ? certificatesK1?.blockedDocumentCount?.countInPage
                      : blockedDocumentCount.countInPage,

                  sumIndex: 6,
                },
              ]}
            />
          )}
        {showActiveCertificates() && certificates.length === 0 && !loading && (
          <EmptyState
            icon="file-text"
            text={t("pages.default.certificates.activeNotFound")}
          />
        )}
        {showActiveCertificates() && loading && (
          <Loading text={t("pages.default.certificates.activeLoading")} />
        )}
      </div>
      {inspectedCertificateId && (
        <ModalCertificate
          t={t}
          certificateId={inspectedCertificateId}
          isOpen={inspectedCertificateId ? true : false}
          toggle={() => setInspectedCertificateId()}
          difCalculate={true}
        />
      )}

      {redemptionCertificates.length > 0 && (
        <>
          <div className="km__p--card">
            <div className="km__p--redemption-operations-header">
              <div className="km__p--redemption-title">
                {t("pages.default.redemption.redemptionOperationsTitle")}
              </div>
            </div>

            <DataTable
              columns={redemptionCertificatesColumns}
              data={redemptionCertificates}
              defaultSortedField={"productionPeriod"}
              defaultSortAsc={false}
              rowKey={"id"}
              pagination
              paginationPerPage={10}
            />
            <div className="km__p--redemption-operations-total-document">
              {t("pages.default.redemption.inputs.totalDocumentAmount")}:{" "}
              <span>
                {redemptionCertificates.reduce((acc, certificate) => {
                  acc += parseInt(
                    certificate.redemptionAmount
                      ? certificate.redemptionAmount
                      : 0
                  );
                  return acc;
                }, 0)}
              </span>
            </div>
          </div>
          <div className="km__p--redemption-operations-inputs-container">
            <div className="km__p--redemption-operations-input-title">
              {t("pages.default.redemption.inputs.beneficiaryInformations")}
            </div>

            <Input
              label={t("pages.default.redemption.inputs.title")}
              type="text"
              value={title}
              disabled={
                organizationType === controlConstants?.K1 ||
                organizationType === controlConstants?.CHARGING_NETWORK_OPERATOR
                  ? true
                  : false
              }
              name="title"
              onChange={beneficiaryInformationsChange}
            />
            <Input
              type="month"
              name="startPeriod"
              label={t("pages.default.redemption.inputs.startPeriod")}
              value={beneficiaryInformations.startPeriod}
              min={datePickerLimitations.startPeriod.min}
              max={datePickerLimitations.startPeriod.max}
              onChange={beneficiaryInformationsChange}
              disabled={
                organizationType === controlConstants?.K1 ? true : false
              }
            />
            <Input
              label={t("pages.default.redemption.inputs.taxId")}
              type="number"
              name="taxId"
              value={
                organizationType === controlConstants?.CHARGING_NETWORK_OPERATOR
                  ? taxId
                  : null
              }
              disabled={
                organizationType === controlConstants?.CHARGING_NETWORK_OPERATOR
                  ? true
                  : false
              }
              min="0"
              maxLength="11"
              onChange={beneficiaryInformationsChange}
            />
            <Input
              type="month"
              name="endPeriod"
              label={t("pages.default.redemption.inputs.endPeriod")}
              value={beneficiaryInformations.endPeriod}
              min={datePickerLimitations.endPeriod.min}
              max={datePickerLimitations.endPeriod.max}
              onChange={beneficiaryInformationsChange}
              disabled={
                organizationType === controlConstants?.K1 ? true : false
              }
            />
            <Select
              options={redemptionPurposeOptions}
              name="redemptionPurpose"
              defaultValue={beneficiaryInformations.redemptionPurpose}
              label={t("pages.default.redemption.inputs.redemptionPurpose")}
              onChange={beneficiaryInformationsChange}
              disabled
            />
            <Select
              options={beneficiaryTypeOptions}
              name="beneficiaryType"
              defaultValue={
                organizationType === controlConstants?.K1
                  ? "K1_PRODUCER"
                  : organizationType ===
                    controlConstants?.CHARGING_NETWORK_OPERATOR
                  ? "PRODUCER"
                  : "CONSUMER"
              }
              label={t("pages.default.redemption.inputs.beneficiaryType")}
              onChange={beneficiaryInformationsChange}
              disabled
            />
            <table width="50%">
              <tr>
                <td>
                  <Select
                    options={cities}
                    name="city"
                    defValue={t("pages.default.redemption.inputs.select")}
                    label={t("pages.default.redemption.inputs.city")}
                    onChange={beneficiaryInformationsChange}
                  />
                  <Select
                    options={districts}
                    name="district"
                    defValue={t("pages.default.redemption.inputs.select")}
                    label={t("pages.default.redemption.inputs.district")}
                    onChange={beneficiaryInformationsChange}
                  />
                  <Textarea
                    label={t("pages.default.redemption.inputs.address")}
                    type="text"
                    name="address"
                    onChange={beneficiaryInformationsChange}
                    value={beneficiaryInformations?.address}
                  />
                </td>
              </tr>
            </table>
            <div>
              <Select
                options={sectorTypeOptions}
                name="sector"
                defValue={t("pages.default.redemption.inputs.select")}
                label={t("pages.default.redemption.inputs.sector")}
                onChange={beneficiaryInformationsChange}
              />
            </div>
            <Textarea
              label={t("pages.default.redemption.inputs.description")}
              type="text"
              name="description"
              onChange={descriptionChange}
              value={description}
              disabled={
                organizationType === controlConstants?.K1 ? true : false
              }
            />
            <div>
              <Button
                onClick={sendRedemptionConfirmationToggle}
                label={t("pages.default.redemption.inputs.sendRedemption")}
              />
            </div>
          </div>
          <ModalConfirmation
            isOpen={sendRedemptionConfirmationModal}
            toggle={() =>
              setSendRedemptionConfirmationModal(
                !sendRedemptionConfirmationModal
              )
            }
            header={t("pages.default.redemption.confirmation.header")}
            body={
              checkRedemptionStatusMessage
                ? checkRedemptionStatusMessage
                : t("pages.default.redemption.confirmation.body")
            }
            approve={sendRedemptionRequest}
            loading={requestLoading}
          />
        </>
      )}
    </>
  );
}
